import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, QueryList, ViewChildren } from '@angular/core';
import { Message, PostMessageResponse } from '@app/core/models/message.model';
import { Publisher } from '@app/core/models/product.model';
import { MessageService } from '@app/core/services/message.service';
import { CassError } from '@core/models/cass-error';
import { AppLocaleService } from '@core/services/app-locale.service';
import { Logger } from '@core/services/logger.service';
import { BehaviorSubject } from 'rxjs';
import { SubSink } from 'subsink';

@Component({
	selector: 'app-chat-panel',
	templateUrl: './chat-panel.component.html',
	styleUrls: ['./chat-panel.component.css'],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class ChatPanelComponent implements OnInit, OnDestroy {

	private readonly COMPONENT_NAME = "ChatPanelComponent";

	@Input()
	orderId?: string;

	@Input()
	messages: Message[] = [];

	@Input()
	currentUserId?: string;

	@Input()
	publisher?: Publisher;

	@Input()
	scrollToMessageId?: string;

	@Output()
	messageSent = new EventEmitter<string>(); // emits the Id of the sent message

	@ViewChildren('message')
	msgHtmlElements?: QueryList<ElementRef>;

	newMessageText?: string;

	sending$ = new BehaviorSubject<"" | "SENDING" | "ERROR">("");

	private subs = new SubSink();

	constructor (public messageService: MessageService,
		private _changeDetectorRef: ChangeDetectorRef,
		private _logger: Logger,
		public appLocaleService: AppLocaleService
	) { }

	ngOnInit(): void {
		if (this.scrollToMessageId && this.messages.some((msg: Message) => msg.id == this.scrollToMessageId)) {
			setTimeout(() => {
				if (this.scrollToMessageId) {
					this.scrollToMessage(this.scrollToMessageId);
				}
			}, 0);
		}
	}

	ngOnDestroy(): void {
		this.subs.unsubscribe();
	}

	scrollToMessage(messageId: string) {
		if (this.msgHtmlElements && this.msgHtmlElements.length > 0) {
			const messageElementId = `message-${messageId}`;

			const scrollElement = this.msgHtmlElements.find((elem: ElementRef) => {
				return elem.nativeElement.id === messageElementId
			});

			if (scrollElement) {
				try {
					scrollElement.nativeElement.scrollIntoView({ behavior: 'smooth' });
				}
				catch (error) {
					this._logger.warning(this.COMPONENT_NAME, `Caught exception when scrolling to the message element. Error: "${error}".`);
				}
			}
		}
	}

	trackByFn_messages(index: number, item: Message) {
		return item.id ?? item.timestamp;
	}

	requestSendMessage() {
		if (this.newMessageText) {
			// TODO handle messages without orderId (new order, not yet saved) when the API allows this
			if (this.orderId) {
				const messageText = this.newMessageText;
				this.newMessageText = "";
				this.sending$.next("SENDING");
				// we don't actually send this message object, but it's an easier way of showing the temporary message in the 
				// chat window while it is being sent. It is replaced with the real message when the list is updated from the server.
				this.messages = [...this.messages, { messageText: messageText, messageType: 1, id: "", senderUserID: this.currentUserId, orderId: this.orderId, customerName: "", campaignName: "", isRead: true, timestamp: "" }];
				this._changeDetectorRef.detectChanges();

				this.subs.add(
					this.messageService.sendMessage(this.orderId, this.publisher?.id ?? '', messageText)
						.subscribe({
							next: (res: PostMessageResponse) => {
								this.sending$.next("");
								this.messageSent.emit(res.messageId);
							},
							error: (error: CassError) => {
								this.sending$.next("ERROR");
							}
						}));
			}
		}
	}
}
